import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react'

import {
  getPortfolio,
  getProfileImage,
  getResume
} from '../api/portfolioApi'

// ─────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────
const AdminContext = createContext(null)

const AUTH_KEY = 'ssp_admin_session'

// ─────────────────────────────────────────────
// Safe Default Structure
// Every sub-property any component reads is
// guaranteed to have a safe typed default so
// .map() / .filter() / .replace() can never
// crash on undefined before data arrives.
// ─────────────────────────────────────────────
export const SAFE_DEFAULTS = {
  hero: {
    name:             '',
    tagline:          '',
    availableForWork: false,
    stats:            []
  },
  about: {
    bio1:  '',
    bio2:  '',
    bio3:  '',
    facts: []
  },
  contact: {
    email:    '',
    phone:    '',
    location: '',
    callout:  ''
  },
  projects:     [],
  certificates: [],
  educations:   [],
  tools:        []
}

// ─────────────────────────────────────────────
// Deep Normaliser
// Merges any partial API response on top of
// SAFE_DEFAULTS so missing keys never reach
// components as undefined.
// ─────────────────────────────────────────────
function normalizeSiteData(raw) {
  if (!raw || typeof raw !== 'object') {
    return SAFE_DEFAULTS
  }

  return {
    hero: {
      ...SAFE_DEFAULTS.hero,
      ...(typeof raw.hero === 'object' && raw.hero !== null ? raw.hero : {}),
      // Guarantee stats is always an array
      stats: Array.isArray(raw.hero?.stats)
        ? raw.hero.stats
        : SAFE_DEFAULTS.hero.stats
    },
    about: {
      ...SAFE_DEFAULTS.about,
      ...(typeof raw.about === 'object' && raw.about !== null ? raw.about : {}),
      // Guarantee facts is always an array
      facts: Array.isArray(raw.about?.facts)
        ? raw.about.facts
        : SAFE_DEFAULTS.about.facts
    },
    contact: {
      ...SAFE_DEFAULTS.contact,
      ...(typeof raw.contact === 'object' && raw.contact !== null ? raw.contact : {})
    },
    projects:     Array.isArray(raw.projects)     ? raw.projects     : [],
    certificates: Array.isArray(raw.certificates) ? raw.certificates : [],
    educations:   Array.isArray(raw.educations)   ? raw.educations   : [],
    tools:        Array.isArray(raw.tools)        ? raw.tools        : []
  }
}

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
export const TOOL_COLORS = [
  'blue', 'purple', 'cyan', 'green',
  'orange', 'yellow', 'pink', 'red'
]

export const TOOL_ICONS = [
  '⚛️', '🟦', '🟢', '☕', '🐍', '🍃', '🗄️', '🐙', '🌿',
  '💙', '📮', '🎨', '🔥', '⚡', '🦀', '🐳', '🔧', '📦',
  '🌐', '🛡️', '💎', '🏗️'
]

export const TOOL_CATS = [
  'Frontend', 'Backend', 'Language', 'Database',
  'Tool', 'DevOps', 'Design', 'Testing'
]

// ─────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────
export function AdminProvider({ children }) {

  // Always start with SAFE_DEFAULTS so every
  // component gets safe initial values
  const [siteData, setSiteData]       = useState(SAFE_DEFAULTS)
  const [profileImage, setProfileImage] = useState(null)
  const [resumeData, setResumeData]   = useState(null)
  const [loading, setLoading]         = useState(true)

  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      return sessionStorage.getItem(AUTH_KEY) === 'true'
    } catch {
      return false
    }
  })

  // ───────────────────────────────────────────
  // Reset to defaults — called from AdminDashboard
  // ───────────────────────────────────────────
  const resetToDefaults = useCallback(() => {
    setSiteData(SAFE_DEFAULTS)
    setProfileImage(null)
    setResumeData(null)
  }, [])

  // ───────────────────────────────────────────
  // Load Portfolio From Backend
  // ───────────────────────────────────────────
  const refreshPortfolio = useCallback(async () => {
    try {
      setLoading(true)

      const [portfolio, image, resume] = await Promise.all([
        getPortfolio(),
        getProfileImage(),
        getResume()
      ])

      // Always normalise — never let a partial API
      // response reach components as-is
      setSiteData(normalizeSiteData(portfolio))
      setProfileImage(image || null)
      setResumeData(resume || null)

    } catch (err) {
      console.error('Portfolio load failed:', err)
      // On error: keep existing state intact.
      // Do NOT reset to empty — show what we had.

    } finally {
      setLoading(false)
    }
  }, [])

  // ───────────────────────────────────────────
  // Initial Load
  // ───────────────────────────────────────────
  useEffect(() => {
    refreshPortfolio()
  }, [refreshPortfolio])

  // ───────────────────────────────────────────
  // Auth
  // ───────────────────────────────────────────
  const login = useCallback((pw) => {
    if (pw === 'admin@Shiv') {
      sessionStorage.setItem(AUTH_KEY, 'true')
      setIsAdmin(true)
      return true
    }
    return false
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(AUTH_KEY)
    setIsAdmin(false)
  }, [])

  // ───────────────────────────────────────────
  // Context Value
  // ───────────────────────────────────────────
  return (
    <AdminContext.Provider
      value={{
        // State
        siteData,
        profileImage,
        resumeData,
        loading,
        isAdmin,

        // Auth
        login,
        logout,

        // Refresh
        refreshPortfolio,

        // Reset (used by AdminDashboard)
        resetToDefaults,

        // Direct setters (used by admin editors)
        setSiteData,
        setProfileImage,
        setResumeData
      }}
    >
      {children}
    </AdminContext.Provider>
  )
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────
export function useAdmin() {
  const ctx = useContext(AdminContext)

  if (!ctx) {
    throw new Error(
      'useAdmin must be used within AdminProvider'
    )
  }

  return ctx
}